import { lazy, Suspense } from 'react'
import type { ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import SiteLayout from '@shared/layouts/SiteLayout'
import ProtectedRoute from '@shared/components/ProtectedRoute'
import DashboardRouter, { RouteLoader } from '@shared/components/DashboardRouter'

import Home from '@features/public/Home'
import AboutUs from '@features/public/AboutUs'
import VisionMission from '@features/public/VisionMission'
import BoardTrustees from '@features/public/BoardTrustees'
import AboutJournal from '@features/public/AboutJournal'
import JournalParticulars from '@features/public/JournalParticulars'
import CallForPapers from '@features/public/CallForPapers'
import EditorialBoard from '@features/public/EditorialBoard'
import EditorialWorkflow from '@features/public/EditorialWorkflow'
import Submission from '@features/public/Submission'
import Guidelines from '@features/public/Guidelines'
import Instructions from '@features/public/Instructions'
import PlagiarismPolicy from '@features/public/PlagiarismPolicy'
import APC from '@features/public/APC'
import Archives from '@features/public/Archives'
import Contact from '@features/public/Contact'
import FAQs from '@features/public/FAQs'
import Subscription from '@features/public/Subscription'
import EventsCollaboration from '@features/public/EventsCollaboration'
import CallForAcademicCollaborations from '@features/public/CallForAcademicCollaborations'
import ReviewersList from '@features/public/ReviewersList'
import DiscussionForum from '@features/public/DiscussionForum'
import ForumPostDetail from '@features/public/ForumPostDetail'
import DocPage from '@features/public/components/DocPage'

const VolumeDetail = lazy(() => import('@features/public/VolumeDetail'))
const ArticleDetail = lazy(() => import('@features/public/ArticleDetail'))
const CurrentIssue = lazy(() => import('@features/public/CurrentIssue'))
const PublishedArticle = lazy(() => import('@features/public/PublishedArticle'))

const LoginPage = lazy(() => import('@features/auth/pages/LoginPage'))
const RegisterPage = lazy(() => import('@features/auth/pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@features/auth/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@features/auth/pages/ResetPasswordPage'))

const withSuspense = (node: ReactNode) => <Suspense fallback={<RouteLoader />}>{node}</Suspense>

const router = createBrowserRouter([
    {
        path: '/',
        element: <SiteLayout />,
        children: [
            { index: true, element: <Home /> },
            { path: 'about-us', element: <AboutUs /> },
            { path: 'vision-mission', element: <VisionMission /> },
            { path: 'board-of-trustees', element: <BoardTrustees /> },
            { path: 'about-the-journal', element: <AboutJournal /> },
            { path: 'journal-particulars', element: <JournalParticulars /> },
            { path: 'call-for-papers', element: <CallForPapers /> },
            { path: 'editorial-board', element: <EditorialBoard /> },
            { path: 'editorial-workflow', element: <EditorialWorkflow /> },
            { path: 'submission', element: <Submission /> },
            { path: 'guidelines', element: <Guidelines /> },
            { path: 'instructions', element: <Instructions /> },
            { path: 'plagiarism-ai-policy', element: <PlagiarismPolicy /> },
            { path: 'article-processing-charge', element: <APC /> },
            { path: 'archives', element: <Archives /> },
            { path: 'archives/:slug', element: withSuspense(<VolumeDetail />) },
            { path: 'archives/:slug/article/:index', element: withSuspense(<ArticleDetail />) },
            { path: 'current-issue', element: withSuspense(<CurrentIssue />) },
            { path: 'published/:id', element: withSuspense(<PublishedArticle />) },
            { path: 'contact-us', element: <Contact /> },
            { path: 'faqs', element: <FAQs /> },

            { path: 'events-collaboration', element: <EventsCollaboration /> },
            { path: 'call-for-academic-collaborations', element: <CallForAcademicCollaborations /> },

            { path: 'discussion/perspective', element: <DiscussionForum category="perspective" /> },
            { path: 'discussion/commentary', element: <DiscussionForum category="commentary" /> },
            { path: 'discussion/:category/:id', element: <ForumPostDetail /> },

            { path: 'reviewers-list', element: <ReviewersList /> },
            { path: 'reviewers-guidelines', element: <DocPage slug="reviewers-guidelines" path="/reviewers-guidelines" /> },

            { path: 'scope-ethical-guidelines', element: <DocPage slug="scope-ethical-guidelines" path="/scope-ethical-guidelines" /> },
            { path: 'publication-ethics', element: <DocPage slug="publication-ethics" path="/publication-ethics" /> },
            { path: 'peer-review-policy', element: <DocPage slug="peer-review-policy" path="/peer-review-policy" /> },
            { path: 'editorial-decision-appeals', element: <DocPage slug="editorial-decision-appeals" path="/editorial-decision-appeals" /> },
            { path: 'correction-retraction', element: <DocPage slug="correction-retraction" path="/correction-retraction" /> },
            { path: 'author-fee-policy', element: <DocPage slug="author-fee-policy" path="/author-fee-policy" /> },

            { path: 'plagiarism-policy', element: <DocPage slug="plagiarism-policy" path="/plagiarism-policy" /> },
            { path: 'privacy-statement', element: <DocPage slug="privacy-statement" path="/privacy-statement" /> },
            { path: 'ugc-guidelines', element: <DocPage slug="ugc-guidelines" path="/ugc-guidelines" /> },
            { path: 'copyright-licensing', element: <DocPage slug="copyright-licensing" path="/copyright-licensing" /> },
            { path: 'cope', element: <DocPage slug="cope" path="/cope" /> },

            { path: 'subscription', element: <Subscription /> }
        ]
    },

    { path: '/dashboard/login', element: withSuspense(<LoginPage />) },
    { path: '/dashboard/register', element: withSuspense(<RegisterPage />) },
    { path: '/dashboard/forgot-password', element: withSuspense(<ForgotPasswordPage />) },
    { path: '/dashboard/reset-password', element: withSuspense(<ResetPasswordPage />) },

    {
        path: '/dashboard/*',
        element: withSuspense(
            <ProtectedRoute>
                <DashboardRouter />
            </ProtectedRoute>
        )
    },

    { path: '*', element: <Navigate to="/" replace /> }
])

export default router
